import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Typography,
  Avatar,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Agriculture as AgricultureIcon,
  Logout as LogoutIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Person as PersonIcon,
  Article as ArticleIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  Storefront as StorefrontIcon // Nouvel import pour l'icône des annonces
} from '@mui/icons-material';
import { useAuth } from '../AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

/**
 * Composant de barre latérale pour le tableau de bord administrateur
 */
const Sidebar = ({ activeSection, setActiveSection, handleLogout }) => {
  const drawerWidth = 240;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const [userData, setUserData] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des données utilisateur:', error);
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  const menuItems = [
    { id: 'dashboard', text: 'Tableau de bord', icon: <DashboardIcon /> },
    { id: 'pending', text: 'En attente d\'approbation', icon: <AgricultureIcon /> },
    { id: 'admin-requests', text: 'Demandes d\'administrateurs', icon: <AdminIcon /> },
    { id: 'administrators', text: 'Administrateurs', icon: <SupervisorAccountIcon /> },
    { id: 'farmers', text: 'Agriculteurs', icon: <PeopleIcon /> },
    { id: 'announcements', text: 'Annonces', icon: <StorefrontIcon /> }, // Nouvel élément de menu
    { id: 'projects', text: 'Projets', icon: <BarChartIcon /> },
    { id: 'statistics', text: 'Statistiques', icon: <BarChartIcon /> },
    { id: 'news', text: 'Actualités', icon: <ArticleIcon /> },
    { id: 'settings', text: 'Paramètres', icon: <SettingsIcon /> },
  ];

  // Ne pas afficher le Sidebar sur mobile car nous utilisons TopBar
  if (!isDesktop) return null;
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        display: { xs: 'none', sm: 'block' },
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          bgcolor: '#014527',
          color: 'white',
          borderRight: '1px solid rgba(255,255,255,0.1)',
          mt: '64px', // Espace pour la TopBar
        },
      }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar
          sx={{
            bgcolor: 'white',
            color: '#014527',
            width: 64,
            height: 64,
            mb: 1
          }}
        >
          <PersonIcon fontSize="large" />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          {userData?.name || 'Administrateur'}
        </Typography>
        {userData?.position && (
          <Typography variant="body2" sx={{ opacity: 0.8, fontStyle: 'italic', mb: 2 }}>
            {userData.position}
          </Typography>
        )}
      </Box>
      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <DashboardIcon sx={{ mr: 1 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Admin AVG
        </Typography>
      </Box>
      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
      <List>
        <ListItem 
          button 
          selected={activeSection === 'dashboard'}
          onClick={() => setActiveSection('dashboard')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText primary="Tableau de bord" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeSection === 'pending'}
          onClick={() => setActiveSection('pending')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <AgricultureIcon />
          </ListItemIcon>
          <ListItemText primary="En attente d'approbation" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeSection === 'admin-requests'}
          onClick={() => setActiveSection('admin-requests')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <AdminIcon />
          </ListItemIcon>
          <ListItemText primary="Demandes d'administrateurs" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeSection === 'administrators'}
          onClick={() => setActiveSection('administrators')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <SupervisorAccountIcon />
          </ListItemIcon>
          <ListItemText primary="Administrateurs" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeSection === 'farmers'}
          onClick={() => setActiveSection('farmers')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <PeopleIcon />
          </ListItemIcon>
          <ListItemText primary="Agriculteurs" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeSection === 'announcements'}
          onClick={() => setActiveSection('announcements')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <StorefrontIcon />
          </ListItemIcon>
          <ListItemText primary="Annonces" />
        </ListItem>

        <ListItem 
          button 
          selected={activeSection === 'diagnostics'}
          onClick={() => setActiveSection('diagnostics')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <AssignmentIcon />
          </ListItemIcon>
          <ListItemText primary="Diagnostics" />
        </ListItem>

        <ListItem 
          button 
          selected={activeSection === 'newsletter'}
          onClick={() => setActiveSection('newsletter')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <EmailIcon />
          </ListItemIcon>
          <ListItemText primary="Newsletter" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeSection === 'projects'}
          onClick={() => setActiveSection('projects')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <BarChartIcon />
          </ListItemIcon>
          <ListItemText primary="Projets" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeSection === 'statistics'}
          onClick={() => setActiveSection('statistics')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <BarChartIcon />
          </ListItemIcon>
          <ListItemText primary="Statistiques" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeSection === 'news'}
          onClick={() => setActiveSection('news')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <ArticleIcon />
          </ListItemIcon>
          <ListItemText primary="Actualités" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeSection === 'settings'}
          onClick={() => setActiveSection('settings')}
          sx={{ 
            '&.Mui-selected': { 
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <ListItemIcon sx={{ color: 'white' }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Paramètres" />
        </ListItem>
      </List>
      <Box sx={{ position: 'absolute', bottom: 0, width: 240, p: 2 }}>
        <Button 
          fullWidth 
          variant="contained" 
          color="error" 
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}
        >
          Déconnexion
        </Button>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
